/**
 * API client do módulo de certificados digitais.
 *
 * As funções principais (listar/upload/toggle) ainda vivem em `api/client.ts`
 * por questão de histórico — novos endpoints do domínio devem ser adicionados
 * aqui, seguindo a orientação de modularização da API.
 */
import { api } from './client';

export interface QrCodeTotpUploadResponse {
  ok: boolean;
  segredo_redigido: boolean;
}

export interface QrCodeTotpStatusResponse {
  pareado: boolean;
  fonte: string | null;
  /** True quando o código gerenciado serve para o login direto na PDPJ (caso "só JurisApp"). */
  codigo_login_direto?: boolean;
}

export interface OtpProvisionarResponse {
  ok: boolean;
  /** otpauth:// do OTP recém-criado na PDPJ — exibir como QR (transitório, não persistir). */
  otpauth_uri: string;
  label: string;
}

export interface OtpRevogarResponse {
  ok: boolean;
  removida_na_pdpj: boolean;
}

export interface OtpQrCodeResponse {
  ok: boolean;
  /** otpauth:// do OTP gerenciado — exibir como QR (transitório, não persistir). */
  otpauth_uri: string;
}

export interface OtpCodigoResponse {
  ok: boolean;
  /** Código TOTP atual de 6 dígitos (transitório, não persistir). */
  codigo: string;
  /** Instante de expiração do código (ISO), para contagem regressiva. */
  valido_ate: string | null;
}

/** Origem (`fonte`) reportada pelo status quando o OTP foi provisionado pelo JurisApp. */
export const FONTE_OTP_GERENCIADO = 'JURISAPP_PROVISIONADO';

/** Query key compartilhada do status de pareamento TOTP (status + invalidação). */
export const TOTP_STATUS_QUERY_KEY = ['certificados', 'qrcode-totp', 'status'] as const;

// O provisionamento/revogação abre um browser headless na PDPJ (login por
// certificado + enrollment do OTP) — pode levar dezenas de segundos. Timeout
// generoso por request para não estourar o default do axios.
const PDPJ_OTP_TIMEOUT_MS = 130_000;

/**
 * Consulta se já existe credencial TOTP pareada para o usuário autenticado.
 *
 * Usado pelo card de upload de QR Code no primeiro render para decidir se
 * exibe o uploader ou o estado de confirmação. O backend consulta o
 * autenticador (fonte de verdade) e responde 200 mesmo quando não há
 * pareamento (retorna `pareado=false`).
 */
export async function fetchQrCodeTotpStatus(): Promise<QrCodeTotpStatusResponse> {
  const { data } = await api.get<QrCodeTotpStatusResponse>(
    '/certificados/qrcode/status',
  );
  return data;
}

/**
 * Faz upload da imagem do QR Code TOTP para o autenticador.
 *
 * Cenário de uso: o advogado já possui TOTP ativo em outro aplicativo
 * autenticador (Google Authenticator, Authy, etc.) associado à PDPJ e
 * precisa compartilhar o segredo com o JurisApp para que possamos assinar
 * e consultar processos em seu nome.
 *
 * @param file Imagem PNG ou JPG do QR Code (máx. 2 MB).
 */
export async function uploadQrCodeTotp(
  file: File,
): Promise<QrCodeTotpUploadResponse> {
  const form = new FormData();
  form.append('arquivo', file);
  const { data } = await api.post<QrCodeTotpUploadResponse>(
    '/certificados/qrcode',
    form,
  );
  return data;
}

/**
 * Conecta-se à PDPJ provisionando um OTP gerenciado pelo JurisApp (aditivo).
 *
 * O autenticador cadastra um segundo fator OTP adicional na conta PDPJ do
 * advogado, captura o segredo e passa a gerar os códigos por ele — sem o
 * usuário precisar compartilhar o QR do app dele. Mantém intacto o OTP que o
 * usuário já tenha. Devolve o `otpauth_uri` para exibirmos um QR (o usuário
 * pode adicioná-lo também ao próprio autenticador).
 *
 * @param codigoOtpAtual Código atual de 6 dígitos do app autenticador do
 *   usuário — exigido no bootstrap (quando o JurisApp ainda não tem segredo
 *   pareado). Omitir quando o usuário não possui OTP prévio na PDPJ.
 */
export async function provisionarOtp(
  codigoOtpAtual?: string,
): Promise<OtpProvisionarResponse> {
  const { data } = await api.post<OtpProvisionarResponse>(
    '/certificados/otp/provisionar',
    { codigo_otp_atual: codigoOtpAtual || null },
    { timeout: PDPJ_OTP_TIMEOUT_MS },
  );
  return data;
}

/**
 * Desconecta-se da PDPJ: revoga o OTP gerenciado pelo JurisApp.
 *
 * Remove a credencial OTP gerenciada na conta da PDPJ e desativa o segredo
 * local. O OTP pessoal do usuário permanece intacto.
 */
export async function revogarOtp(): Promise<OtpRevogarResponse> {
  const { data } = await api.post<OtpRevogarResponse>(
    '/certificados/otp/revogar',
    undefined,
    { timeout: PDPJ_OTP_TIMEOUT_MS },
  );
  return data;
}

/**
 * Exporta o QR Code do OTP gerenciado pelo JurisApp para o usuário autenticado.
 *
 * Re-expõe sob demanda o `otpauth_uri` do segredo já armazenado, para o advogado
 * adicioná-lo ao próprio aplicativo autenticador. Leitura rápida e auditada no
 * backend; o ownership é garantido pelo CPF do usuário logado (nunca informado
 * pelo cliente). Exibir o QR de forma transitória — nunca persistir o segredo.
 */
export async function exportarOtpQrCode(): Promise<OtpQrCodeResponse> {
  const { data } = await api.post<OtpQrCodeResponse>('/certificados/otp/qrcode');
  return data;
}

/**
 * Gera o código TOTP atual do OTP gerenciado, para o advogado entrar direto na PDPJ.
 *
 * Caso de uso: o advogado cujo único autenticador é o JurisApp precisa do código
 * de 6 dígitos para um login manual no portal. Leitura sensível e auditada; o
 * ownership é garantido pelo CPF do usuário logado. Exibir de forma transitória.
 */
export async function gerarCodigoOtp(): Promise<OtpCodigoResponse> {
  const { data } = await api.post<OtpCodigoResponse>('/certificados/otp/codigo');
  return data;
}
